import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import React, { useEffect, useRef, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import { Input, InputGroup } from "./input";

const AutocompleteInput = ({
  options,
  selected,
  onSelect,
  value,
  onChange,
  actionBefore,
  loading,
  disabled = false,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [showOptions, setShowOptions] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options, actionBefore]);

  useEffect(() => {
    setSelectedOption(
      selected
        ? options?.find((option) => option.value === selected.value)
        : null,
    );
  }, [selected, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setShowOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelection = async (option) => {
    if (option.onAction) {
      await option.onAction(); // Execute the actionBefore action
      setShowOptions(false); // Hide the options list after action
    } else {
      onChange(option.label);
      setSelectedOption(option);
      setShowOptions(false);
      onSelect(option);
    }
  };

  const handleInputClick = () => {
    setShowOptions(true);
    onChange("");
  };

  const showActionBefore =
    actionBefore && typeof actionBefore === "object" && actionBefore.content;

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && actionBefore) {
      e.preventDefault(); // Prevent the default action if needed
      handleSelection(actionBefore); // Trigger the actionBefore action
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <InputGroup>
        <MagnifyingGlassIcon />
        <Input
          type="text"
          value={value}
          disabled={disabled}
          onChange={(e) => {
            onChange(e.target.value);
            const newFilteredOptions = options?.filter((option) =>
              option.label.toLowerCase().includes(e.target.value.toLowerCase()),
            );
            setFilteredOptions(newFilteredOptions);
          }}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder="Search"
          autoComplete="off"
        />
      </InputGroup>
      {showOptions && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-80 overflow-y-auto">
          {showActionBefore && (
            <li
              key={`actionBefore-${actionBefore.value}`} // Ensure unique key
              onClick={() => handleSelection(actionBefore)}
              className="px-4 py-2 cursor-pointer hover:bg-gray-100 hover:text-gray-900"
            >
              {actionBefore.label}
              {actionBefore.content && (
                <>
                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <>
                      <span>{actionBefore.accessibilityLabel}</span>
                      <div>{actionBefore.content}</div>
                    </>
                  )}
                </>
              )}
            </li>
          )}
          {filteredOptions?.map((option, index) => (
            <li
              key={`option-${option.value}-${index}`} // Ensure unique key
              onClick={() => !option.disabled && handleSelection(option)}
              className={`px-4 py-2 ${
                option.disabled
                  ? "cursor-not-allowed text-gray-400 bg-gray-50"
                  : "cursor-pointer hover:bg-gray-100 hover:text-gray-900"
              } ${
                selectedOption && selectedOption.value === option.value
                  ? "bg-yellow text-white"
                  : ""
              }`}
            >
              {option.label}
              {option.content && (
                <>
                  <span>{option.accessibilityLabel}</span>
                  <div>{option.content}</div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AutocompleteInput;
